<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useCompaniesStore } from 'dashboard/stores/companies';

import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import CompaniesTable from 'dashboard/components-next/Companies/CompaniesTable.vue';
import CompaniesEmptyState from 'dashboard/components-next/Companies/CompaniesEmptyState.vue';
import ContactsListSkeleton from 'dashboard/components-next/Contacts/Pages/ContactsListSkeleton.vue';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';
import CompanyCreateDialog from 'dashboard/components-next/Companies/CompanyCreateDialog.vue';
import CompanyDrawer from 'dashboard/components-next/Companies/CompanyPanel/CompanyDrawer.vue';

const DEFAULT_SORT_FIELD = 'name';
const DEBOUNCE_DELAY = 300;

const companiesStore = useCompaniesStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

// The list shows what the last list fetch brought. The open company is loaded with companiesStore.show, which adds
// it to the same records: a company of page 3 opened by a link would otherwise become an extra row of page 1.
const listIds = ref(null);
const companies = computed(() => {
  const records = companiesStore.getCompaniesList;
  return listIds.value
    ? records.filter(record => listIds.value.has(record.id))
    : records;
});
const meta = computed(() => companiesStore.getMeta);
const uiFlags = computed(() => companiesStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const createCompanyDialogRef = ref(null);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { companies_sort_by: companySortBy = DEFAULT_SORT_FIELD } =
  uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(companySortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeSort = computed(() => sortState.activeSort);
const activeOrdering = computed(() => sortState.activeOrdering);

const isFetchingList = computed(() => uiFlags.value.fetchingList);
const isCreatingCompany = computed(() => uiFlags.value.creatingItem);

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const sortParam = computed(() => buildSortAttr());

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchCompanies = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortParam.value;

  // Only update URL if arguments were explicitly provided
  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  const records = currentSearch
    ? await companiesStore.search({
        search: currentSearch,
        page: currentPage,
        sort: currentSort,
      })
    : await companiesStore.get({
        page: currentPage,
        sort: currentSort,
      });
  if (Array.isArray(records))
    listIds.value = new Set(records.map(record => record.id));
  // A list fetch replaces the records: the open company (not on this page) comes back for the drawer.
  const openId = route.params.companyId;
  if (openId && !companiesStore.getRecord(openId).id)
    companiesStore.show(openId);
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchCompanies(1, query, sortParam.value);
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  fetchCompanies(page, searchValue.value, sortParam.value);
};

// ---- The company drawer (fase 5.6, R2-5-PC-9 = A, the contact's Q4 rule) --------------------------------------
// The open company is the route's `companyId`; opening keeps the list's query (page, search, sort), switching
// replaces the entry, closing goes back to the list. Leaving an edited profile asks first (R2-5-PC-4 = B).
const companyDrawerRef = ref(null);
const openCompanyId = computed(() => route.params.companyId || null);

const listRoute = () => ({
  name: 'companies_dashboard_index',
  params: { accountId: route.params.accountId },
  query: route.query,
});

const showCompany = companyId => {
  const target = {
    name: 'companies_dashboard_show',
    params: { accountId: route.params.accountId, companyId },
    query: route.query,
  };
  if (openCompanyId.value) router.replace(target);
  else router.push(target);
};

let skipGuard = false;
const closeCompany = async ({ force = false } = {}) => {
  if (!openCompanyId.value) return;
  skipGuard = force && !!companyDrawerRef.value?.isDirty();
  const list = router.resolve(listRoute());
  if (window.history.state?.back === list.fullPath) router.back();
  else await router.push(list);
};

const guardCompanyChange = (to, from) => {
  if (skipGuard) {
    skipGuard = false;
    return true;
  }
  const sameCompany =
    String(to.params.companyId ?? '') === String(from.params.companyId ?? '');
  if (sameCompany || !companyDrawerRef.value?.isDirty()) return true;
  companyDrawerRef.value.runGuarded(() => {
    skipGuard = true;
    router.push(to.fullPath);
  });
  return false;
};
onBeforeRouteUpdate(guardCompanyChange);
onBeforeRouteLeave(guardCompanyChange);

const openCreateCompanyDialog = () => {
  createCompanyDialogRef.value?.open();
};

const createCompany = async company => {
  try {
    const newCompany = await companiesStore.create(company);
    listIds.value?.add(newCompany.id);
    createCompanyDialogRef.value?.onSuccess();
    useAlert(t('COMPANIES.CREATE.MESSAGES.SUCCESS'));
    showCompany(newCompany.id);
  } catch {
    useAlert(t('COMPANIES.CREATE.MESSAGES.ERROR'));
  }
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    companies_sort_by: buildSortAttr(),
  });

  fetchCompanies(1, searchValue.value, buildSortAttr());
};

onMounted(() => {
  searchValue.value = searchQuery.value;

  if (!route.query.sort && sortParam.value !== DEFAULT_SORT_FIELD) {
    updateURLParams(pageNumber.value, searchQuery.value, sortParam.value);
  }

  fetchCompanies();
});
</script>

<template>
  <CompaniesListLayout
    :search-value="searchValue"
    :header-title="t('COMPANIES.HEADER')"
    :count="isFetchingList ? null : Number(meta.totalCount || 0) || null"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :show-pagination-footer="!isFetchingList && !!companies.length"
    @update:current-page="onPageChange"
    @update:sort="handleSort"
    @search="onSearch"
    @create="openCreateCompanyDialog"
  >
    <ContactsListSkeleton
      v-if="isFetchingList"
      :label="t('COMPANIES_POLARIS.LIST.LOADING')"
    />
    <CompaniesEmptyState
      v-else-if="companies.length === 0 && !searchValue"
      @create="openCreateCompanyDialog"
    />
    <Empty
      v-else-if="companies.length === 0"
      class="py-16"
      data-companies-no-result
    >
      <EmptyHeader>
        <EmptyTitle>{{ t('COMPANIES.EMPTY_STATE.TITLE') }}</EmptyTitle>
        <EmptyDescription>
          {{ t('COMPANIES_POLARIS.EMPTY.NO_RESULT') }}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
    <CompaniesTable
      v-else
      :companies="companies"
      :caption="t('COMPANIES.HEADER')"
      :active-sort="activeSort"
      :active-ordering="activeOrdering"
      :open-company-id="openCompanyId"
      @sort="handleSort"
      @show-company="showCompany"
    />
    <CompanyCreateDialog
      ref="createCompanyDialogRef"
      :is-loading="isCreatingCompany"
      @create="createCompany"
    />
    <template #drawer>
      <CompanyDrawer
        ref="companyDrawerRef"
        :company-id="openCompanyId"
        @close="closeCompany"
      />
    </template>
  </CompaniesListLayout>
</template>
